'use client';

import React, { PropsWithChildren, ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sheet, SheetRef } from 'react-modal-sheet';
import { useTranslations } from 'next-intl';
import { clsx } from 'clsx';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { useDisclosure } from '@reactuses/core';
import useModal from '@/hooks/useModal';
import { useDynamicHeightContext } from '@/context/DynamicHeightContext';
import Button, { FontAwesomeButton } from '@/components/common/Button';
import Flexbox from './Flexbox';
export type ModalSize = 'full' | 'lg' | 'md' | number | number[];
export interface BottomSheetModalProps {
  show: boolean;
  onClose?: () => void;
  onSure?: () => void;
  delay?: number;
  title?: string;
  titleRight?: ReactNode;
  titleLeft?: ReactNode;
  size?: ModalSize;
  noCloseButton?: boolean;
  noSureButton?: boolean;
  contentClassName?: string;
  containerRef?: RefObject<HTMLElement | null>;
  contentRef?: RefObject<HTMLElement | null>;
  zIndex?: number;
  notScrollable?: boolean;
  testIdPrefix?: string;
  headerClassNameOverrider?: string;
  disableDrag?: boolean;
}
const BottomSheetModal: React.FC<PropsWithChildren<BottomSheetModalProps>> = ({
  title,
  titleLeft,
  titleRight,
  children,
  show,
  size = 'lg',
  onClose,
  onSure,
  noCloseButton,
  noSureButton,
  delay,
  contentClassName,
  containerRef,
  contentRef,
  zIndex = 100,
  notScrollable,
  testIdPrefix = '',
  headerClassNameOverrider,
  disableDrag
}) => {
  const t = useTranslations();
  const [isMounted, setIsMounted] = useState(false);
  const ref = useRef<SheetRef>(null);
  const {
    shouldVisible
  } = useModal(show, delay);
  const {
    isOpen: isReallyClosed,
    onOpen: setIsReallyClosed,
    onClose: setIsNotReallyClosed
  } = useDisclosure();
  const {
    isHeightChanged
  } = useDynamicHeightContext();
  useEffect(() => {
    if (shouldVisible) setIsNotReallyClosed();
  }, []);
  const ContentComponent = notScrollable ? Sheet.Content : Sheet.Scroller;

  // 負數代表距頂端距離、小數代表比例、正數代表實際高度
  const snapPoints = useMemo(() => {
    switch (size) {
      case 'full':
        return [-1];
      case 'lg':
        return [-16];
      case 'md':
        return [-180];
      default:
        return Array.isArray(size) ? size : [size];
    }
  }, [size]);
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);
  const handleSure = useCallback(() => {
    if (onSure) {
      onSure();
    } else {
      onClose?.();
    }
  }, [onSure, onClose]);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  useEffect(() => {
    if (shouldVisible) {
      ref.current?.snapTo(0);
    }
  }, [shouldVisible]);
  const testId = useMemo(() => {
    if (testIdPrefix) {
      return `${testIdPrefix}bottom-sheet-modal`;
    } else {
      return 'bottom-sheet-modal';
    }
  }, [testIdPrefix]);
  if (!isMounted) {
    return null;
  }
  return <Sheet ref={ref} isOpen={shouldVisible} onClose={handleClose} snapPoints={snapPoints} initialSnap={0} style={{
    zIndex: isReallyClosed ? '-1' : zIndex
  }} id={'modal-sheet'} onOpenStart={setIsNotReallyClosed} onCloseEnd={setIsReallyClosed} data-test-id={`${testId}`} disableDrag={disableDrag} data-sentry-element="Sheet" data-sentry-component="BottomSheetModal" data-sentry-source-file="BottomSheetModal.tsx">
      <Sheet.Container className={clsx('rounded-t-l', 'pt-4', {
      'pb-[22px]': isHeightChanged
    })} ref={containerRef} data-test-id={`${testId}-container`} style={{
      borderTopRightRadius: 16,
      borderTopLeftRadius: 16,
      boxShadow: '0px 8px 24px 0px rgba(0, 0, 0, 0.10)'
    }} data-sentry-element="unknown" data-sentry-source-file="BottomSheetModal.tsx">
        <Sheet.Header className={headerClassNameOverrider ?? 'py-0.5 px-4'} data-test-id={`${testId}-header`} data-sentry-element="unknown" data-sentry-source-file="BottomSheetModal.tsx">
          <Flexbox justify={'between'} align={'center'} data-sentry-element="Flexbox" data-sentry-source-file="BottomSheetModal.tsx">
            {titleLeft ? titleLeft : !noCloseButton ? <Button data-test-id={`${testId}-close-button`} buttonType={'tertiary'} disableSize disableRadius className={clsx('text-fz5/text', 'py-1.5 px-3', 'rounded-full')} onClick={handleClose}>
                {t('common.button.關閉')}
              </Button> : (
          /**
           * Placeholder for keeping the title centered
           */
          <Flexbox align={'center'} justify={'center'} className={'w-10 h-10'} />)}
            <h2 className={'font-bold'}>{title}</h2>
            {titleRight ? titleRight : !noSureButton ? <Flexbox align={'center'} justify={'center'} className={'w-10 h-10'} data-test-id={`${testId}-sure-button`}>
                <FontAwesomeButton icon={faCheck} iconClassName={'text-primary text-[24px]'} onClick={handleSure} />
              </Flexbox> : (
          /**
           * Placeholder for keeping the title centered
           */
          <Flexbox align={'center'} justify={'center'} className={'w-10 h-10'} />)}
          </Flexbox>
        </Sheet.Header>
        <ContentComponent ref={contentRef} className={clsx(contentClassName, !notScrollable && 'overflow-scroll')} data-test-id={`${testId}-content`} data-sentry-element="ContentComponent" data-sentry-source-file="BottomSheetModal.tsx">
          {children}
        </ContentComponent>
      </Sheet.Container>
      {/*@ts-ignore*/}
      <Sheet.Backdrop onClick={e => e.stopPropagation()} data-sentry-element="unknown" data-sentry-source-file="BottomSheetModal.tsx" />
    </Sheet>;
};
export default BottomSheetModal;